import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface VectorShiftTestProps {
  onBack: () => void;
}

export default function VectorShiftTest({ onBack }: VectorShiftTestProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [resumeText, setResumeText] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [additionalDocs, setAdditionalDocs] = useState("");
  const [textProcessed, setTextProcessed] = useState(false);
  const { toast } = useToast();

  const handleProcessText = () => {
    if (!resumeText.trim()) {
      toast({
        title: "Resume Text Required",
        description: "Please enter resume text first",
        variant: "destructive",
      });
      return;
    }
    setTextProcessed(true);
    toast({
      title: "Text Processed",
      description: "Resume text is ready for testing",
    });
  };

  const handleTest = async () => {
    if (!textProcessed || !resumeText.trim()) {
      toast({
        title: "Process Text First",
        description: "Please process your resume text before testing",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('vectorshift-test', {
        body: {
          Resume: resumeText,
          Target_Job_Title: jobTitle || "",
          Additional_Documents: additionalDocs || "No additional documents"
        }
      });
      
      console.log('Test response:', { data, error });
      setResult(data);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: data.success ? "Test Successful" : "Test Failed",
        description: data.success ? "VectorShift API is working" : `Error: ${data.error}`,
        variant: data.success ? "default" : "destructive",
      });
    } catch (error) {
      console.error('Test error:', error);
      toast({
        title: "Test Failed", 
        description: "Failed to test VectorShift API",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Button>
        <div>
          <h1 className="text-3xl font-bold">VectorShift API Test</h1>
          <p className="text-muted-foreground">Test basic VectorShift API connectivity</p>
        </div>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>API Connection Test</CardTitle>
            <CardDescription>Test VectorShift API with text input</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="resumeText">Resume Text</Label>
              <Textarea
                id="resumeText"
                placeholder="Paste your resume text here..."
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                rows={8}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="jobTitle">Target Job Title (Optional)</Label>
              <Input
                id="jobTitle"
                placeholder="Job title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="additionalDocs">Additional Documents (Optional)</Label>
              <Textarea
                id="additionalDocs"
                placeholder="Any additional context or documents..."
                value={additionalDocs}
                onChange={(e) => setAdditionalDocs(e.target.value)}
                rows={3}
              />
            </div>
            
            <Button 
              onClick={handleProcessText}
              disabled={!resumeText.trim() || textProcessed}
              className="w-full"
              variant="outline"
            >
              {textProcessed ? "Text Processed ✓" : "Process Text"}
            </Button>
            
            {textProcessed && (
              <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Resume text processed and ready for testing ({resumeText.length} characters)
                </p>
              </div>
            )}
            
            <Button 
              onClick={handleTest}
              disabled={isLoading || !textProcessed}
              className="w-full"
            >
              {isLoading ? "Testing..." : "Test VectorShift API"}
            </Button>
          </CardContent>
        </Card>

        {result && result.success && result.data && !result.data.error && (
          <Card>
            <CardHeader>
              <CardTitle>Output</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-sm whitespace-pre-wrap">
                {(() => {
                  try {
                    const outputs = result.data.outputs;
                    if (outputs && outputs.Test_1) {
                      const parsedOutput = JSON.parse(outputs.Test_1);
                      return parsedOutput.message || outputs.Test_1;
                    }
                    return typeof result.data === 'string' ? result.data : JSON.stringify(result.data, null, 2);
                  } catch (e) {
                    return typeof result.data === 'string' ? result.data : JSON.stringify(result.data, null, 2);
                  }
                })()}
              </pre>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}